import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FieldValues, Path, UnPackAsyncDefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useList } from 'react-use';
import { useGetConfirmationRequestAddFile } from '../../../../api/confirmation-requests/confirmation-requests';
import { useDefaultOptions } from '../../../../hooks/api';
import ErrorModal from '../../../modules/ErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';

export interface FileWithId extends File {
  id?: number;
}

interface Props<T extends FieldValues> {
  id?: string | undefined;
  name: Path<UnPackAsyncDefaultValues<T>>;
  setValue: (name: Path<UnPackAsyncDefaultValues<T>>, next: FileWithId[]) => void;
  // eslint-disable-next-line react/require-default-props
  selectedFiles?: FileWithId[];
  historyId: number;
  refetchDetail: () => void;
}

/**
 * ファイルをドラッグアンドドロップしてアップロードするためコンポーネント
 * 参考：https://react-dropzone.js.org/
 * @param
 * @returns
 */
function ProductionConfirmationRequestEditAddFile<T extends FieldValues>({
  id,
  name,
  setValue,
  selectedFiles = [],
  historyId,
  refetchDetail,
}: Props<T>) {
  // 選択済みのファイル一覧を表示
  const [acceptedFiles, { clear, push: pushAcceptedFiles }] = useList<FileWithId>(selectedFiles);

  // ファイルがドロップされたときは、選択済みファイルリストに追加
  const onDrop = useCallback(
    (files: FileWithId[]) => {
      pushAcceptedFiles(...files);
    },
    [pushAcceptedFiles]
  );

  // ドロップ可能エリアを作るのに使うプロパティを取得
  const { getRootProps, getInputProps, isDragAccept } = useDropzone({ onDrop });

  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 選択済みファイルリストが変更された場合に呼び出される
  useEffect(() => setValue(name, acceptedFiles), [name, acceptedFiles, setValue]);

  const options = useDefaultOptions(language);

  // ファイル追加API
  const [dispErrorModal, setDispErrorModal] = useState(false);
  const { mutateAsync, isLoading, isError, error } = useGetConfirmationRequestAddFile(options);
  useEffect(() => setDispErrorModal(isError), [isError]);

  const onClickAddFile = async () => {
    if (!acceptedFiles) {
      return;
    }
    await mutateAsync(
      {
        confirmationRequestHistoryId: historyId,
        data: { 'files[]': acceptedFiles },
      },
      {
        onSuccess: () => {
          clear(); // ドロップファイルをクリア
          refetchDetail(); // 再描画
        },
      }
    );
  };

  return (
    <div className="mt10 fa1_confreq" style={{ flexBasis: '50%' }}>
      <label htmlFor="">{t('添付ファイル')}</label>
      <div id="upFileWrap" className={acceptedFiles.length > 0 ? 'selected' : ''} {...getRootProps()}>
        <label
          id="dropArea"
          className={isDragAccept ? 'dragover' : undefined}
          htmlFor="uploadFile"
          style={{ display: 'flex' }}
        >
          <div className="fileDropLeft" />
          <div className="fileDropCenter">
            <i className="fa-solid fa-arrow-up-from-bracket" />
            <span className="fileDropOr">{t('ファイルをドロップ　または　')}</span>
            <input type="file" id={id} name={name} {...getInputProps()} />
            <div>
              <span className="fileselecttxt">{t('ファイルを選択')}</span>
            </div>
          </div>
          {/* ドロップされたファイル名を表示する */}
          <div className="fileDropRight">
            {acceptedFiles.map((x, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div style={{ margin: '2px' }} key={`${name}-${i}`}>
                <span className="selectfile">
                  {['xls', 'xlsx', 'xlsm', 'xltx', 'xltm'].indexOf(String(x.name.split('.').pop())) !== -1 && (
                    <i className="fa fa-file-excel" />
                  )}
                  {['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm'].indexOf(String(x.name.split('.').pop())) !== -1 && (
                    <i className="fa fa-file-word" />
                  )}
                  {['ppt', 'pptx', 'pptm', 'ppsx', 'ppsm', 'pps', 'potx', 'potm', 'pot'].indexOf(
                    String(x.name.split('.').pop())
                  ) !== -1 && <i className="fa fa-file-powerpoint" />}
                  {x.name.split('.').pop() === 'csv' && <i className="fa fa-file-csv" />}
                  {x.name.split('.').pop() === 'zip' && <i className="fa fa-file-archive" />}
                  {x.name.split('.').pop() === 'pdf' && <i className="fa fa-file-pdf" />}
                  {x.name.split('.').pop() === 'txt' && <i className="fa fa-file-text" />}
                  {['png', 'jpg', 'jpeg', 'eps'].indexOf(String(x.name.split('.').pop())) !== -1 && (
                    <i className="fa fa-file-image" />
                  )}
                  {[
                    'xls',
                    'xlsx',
                    'xlsm',
                    'xltx',
                    'xltm',
                    'doc',
                    'docx',
                    'docm',
                    'dot',
                    'dotx',
                    'dotm',
                    'ppt',
                    'pptx',
                    'pptm',
                    'ppsx',
                    'ppsm',
                    'pps',
                    'potx',
                    'potm',
                    'pot',
                    'csv',
                    'zip',
                    'pdf',
                    'txt',
                    'png',
                    'jpg',
                    'jpeg',
                    'eps',
                  ].indexOf(String(x.name.split('.').pop())) === -1 && <i className="fa fa-file" />}
                  {x.name}
                </span>
              </div>
            ))}
          </div>
        </label>
      </div>
      <div className="mt5" style={{ textAlign: 'end' }}>
        <span>
          <input
            type="button"
            className="btn1 lsp1"
            style={{ width: '100px' }}
            value={t('追加').toString()}
            onClick={() => {
              // アップロード
              void onClickAddFile();
            }}
          />
        </span>
      </div>
      {isLoading && <SpinnerModal />}
      {dispErrorModal && <ErrorModal error={error} onClickOk={() => setDispErrorModal(false)} />}
    </div>
  );
}

ProductionConfirmationRequestEditAddFile.defaultProps = {
  id: undefined,
};

export default ProductionConfirmationRequestEditAddFile;
